import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectSwitchFailed, selectSwitchingTo } from "../../features/contexts/contexts-slice";
import { ContextKind } from "../../features/contexts/enums/ContextKind";
import { useContextDetail } from "../../features/contexts/hooks/useContextDetail";
import { useContexts } from "../../features/contexts/hooks/useContexts";
import { switchPortalContext } from "../../features/contexts/slice-extensions/contexts-slice-extension";
import { selectPortalSession } from "../../features/portal/portal-slice";
import { ContextSwitcherItem, ContextSwitcherMenu } from "../ContextSwitcher";

/**
 * The portal header's switcher: the other member rows the email reaches (from the redeem, code or
 * switch response) and, when an instructor sign-in is stored next to the portal one, "Back to
 * <school>". Nothing is shown for a member with one row and no instructor sign-in.
 */
const PortalContextSwitcher = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const detailOf = useContextDetail();
  const session = useAppSelector(selectPortalSession);
  const switchingTo = useAppSelector(selectSwitchingTo);
  const switchFailed = useAppSelector(selectSwitchFailed);
  const { contexts, currentKey, switchTo } = useContexts();
  const rows = session?.contexts ?? [];
  const current = rows.find((row) => row.memberId === session?.memberId);
  if (!session || !current) return null;

  const memberItems: ContextSwitcherItem[] = rows
    .filter((row) => row !== current)
    .map((row) => ({
      key: row.memberId,
      title: row.organizationName,
      detail: detailOf({ kind: ContextKind.Member, wardCount: row.wardCount }),
      onSelect: () => void dispatch(switchPortalContext({ memberId: row.memberId })),
    }));
  const instructorContext = contexts.find((context) => context.key === currentKey);
  const backItem: ContextSwitcherItem[] = instructorContext
    ? [
        {
          key: instructorContext.key,
          title: t("contexts.backTo", { organization: instructorContext.organizationName }),
          detail: detailOf(instructorContext),
          // Re-issued, so the server remembers the instructor side for the next sign-in too.
          onSelect: () => switchTo(instructorContext),
        },
      ]
    : [];

  return (
    <ContextSwitcherMenu
      title={current.organizationName}
      detail={detailOf({ kind: ContextKind.Member, wardCount: current.wardCount })}
      items={[...memberItems, ...backItem]}
      busy={switchingTo !== null}
      failed={switchFailed}
    />
  );
};

export default PortalContextSwitcher;
